import { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Full-screen mobile drawer that slides up from the bottom.
 * Used for Chapters and Cast panels on mobile.
 */
export default function MobileDrawer({ 
  isOpen, 
  onClose, 
  title, 
  icon: Icon,
  children 
}) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`
          lg:hidden fixed inset-0 bg-ink/50 z-50
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div 
        className={`
          lg:hidden fixed inset-x-0 bottom-0 top-0 z-50
          bg-cream rounded-t-2xl
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        `}
      >
        {/* Header */}
        <div className="sticky top-0 bg-cream border-b border-parchment px-4 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {Icon && (
                <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-rust" />
                </div>
              )}
              <h2 className="font-display text-xl font-bold text-ink">
                {title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-sepia hover:text-ink hover:bg-parchment/50 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          {/* Drag handle indicator */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-parchment rounded-full" />
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-72px)] px-4 py-4">
          {children}
        </div>
      </div>
    </>
  );
}


